/**
 * Endringslogg (#267 SET-32): who changed which company setting, when, and from what to what, newest
 * first, 50 at a time (GET /settings/history). Applied changes only: a refused or failed save writes no
 * row. Covers the company row (PUT /tenant/:id) and, since 2026-11-04 (owner), pay types, a person's
 * pay and role, the staffing need and roles; `kind` and `subject` say which. Rate rules, employer costs
 * and the workspace switches are saved elsewhere and are not in it, which the panel says.
 */
import { get } from '@/api/client';
import { T } from '@/i18n';
import { Button, Empty, LoadError, Panel, Skeleton } from '@/ui';
import { fmtD } from '@/ui/format';
import { useInfiniteQuery } from '@tanstack/react-query';

export interface SettingChange {
  id: number;
  field: string;
  before: unknown;
  after: unknown;
  at: string;
  who: string | null;
  /** null: the company row; otherwise what the row is about, by name (a person's read at the time of asking) */
  kind?: 'pay_type' | 'person' | 'role' | null;
  subject?: string | null;
  subject_id?: number | null;
  /** PROM-17: the payroll system the value was taken from; null when saved in Timefly */
  source?: 'poweroffice' | 'tripletex' | null;
}

/** the field as the page names it; an unknown field shows its key rather than nothing */
const FIELD: Record<string, string> = {
  name: 'Navn',
  org_number: 'Organisasjonsnummer',
  tariff_bound: 'Tariffavtale',
  subscription_type: 'Abonnement',
  availability_requires_approval: 'Tilgjengelighet må godkjennes',
  projects_required: 'Prosjekt påkrevd på vakter',
  projects_label: 'Ordet for prosjekt',
  budget_warn_percent: 'Varsel ved budsjett, prosent',
  is_gps_enabled: 'GPS på stempling',
  gps_radius_meters: 'Radius, meter',
  latitude: 'Breddegrad',
  longitude: 'Lengdegrad',
  shift_length_hours: 'Avtalt daglig arbeidstid',
  agreed_week_hours: 'Avtalt ukentlig arbeidstid',
  overtime_period_start: 'Start på 52-ukersperioden',
  opening_hours: 'Åpningstider',
  break_mode: 'Pause',
  lunch_included: 'Pause betalt',
  lunch_length_minutes: 'Pauselengde, minutter',
  vacation_day_basis: 'Feriedager telles som',
  payroll_system: 'Lønnssystem',
  regular_pay_code: 'Lønnsart for vanlige timer',
  hoytid_pay_code: 'Lønnsart for tillegg 1. og 17. mai',
  exempt_salaried_no_supplements: 'Ingen tillegg for fastlønte unntatt arbeidstidsreglene',
  merarbeid_plain_pay: 'Merarbeid betales som ordinær lønn',
  avspasering_unused: 'Ubrukte timer',
  avspasering_payout_day: 'Utbetalingsdato for ubrukte timer',
  avspasering_payout_code: 'Lønnsart for utbetalt avspasering',
  push_settings: 'Varsler',
  staffing_need: 'Bemanningsbehov',
  // a pay type, a person's pay and role, a role (owner 2026-11-04)
  code: 'Kode',
  calculation_type: 'Beregning',
  rate_value: 'Sats',
  yields_to_payroll_type_id: 'Betales ikke i timer med',
  pays_whole_hour: 'Betaler hele timen, grunnlønn og tillegg',
  contract_type: 'Lønnstype',
  hourly_rate: 'Timelønn, kroner per time',
  monthly_rate: 'Månedslønn, kroner',
  // the date a person's new pay applies from, the rate history's row (2026-11-04)
  rate_valid_from: 'Lønn gjelder fra',
  role: 'Rolle',
  permissions: 'Tilganger',
  // a person's contract in the company (PROM-17)
  employment_form: 'Ansettelsesform',
  employment_start: 'Ansatt fra',
  end_date: 'Sluttdato',
};
const WORD: Record<string, Record<string, string>> = {
  contract_type: { hourly: 'Timelønn', monthly: 'Fastlønn' },
  calculation_type: { extra_per_hour: 'Kroner per time', percent: 'Prosent' },
  // a new company's start (2026-11-04); null stays «tom», «Annet, eller vet ikke»
  payroll_system: { unchosen: 'Ikke valgt ennå' },
  employment_form: { fast: 'Fast', midlertidig: 'Midlertidig', tilkalling: 'Tilkalling' },
};
const SOURCE: Record<string, string> = { poweroffice: 'PowerOffice', tripletex: 'Tripletex' };

/** the field as the page names it; an unknown field shows its key */
export const fieldLabel = (field: string): string => (FIELD[field] ? T(FIELD[field] as string) : field);

/** who made the change, and the payroll system it was taken from when an import wrote it (PROM-17) */
export function whoText(e: Pick<SettingChange, 'who' | 'source'>): string {
  const who = e.who ?? T('Ukjent bruker');
  const system = e.source ? (SOURCE[e.source] ?? e.source) : null;
  return system ? T('{who}, hentet fra {system}', { who, system }) : who;
}

const grid = (v: unknown) => v !== null && typeof v === 'object' && !Array.isArray(v);

/** what the row says changed: the field, after the pay type, person or role it belongs to */
export function changeLabel(e: SettingChange): string {
  const field = fieldLabel(e.field);
  if (!e.kind) return field;
  const name = e.subject ?? '';
  const of =
    e.kind === 'pay_type'
      ? T('Lønnsart «{name}»', { name })
      : e.kind === 'role'
        ? T('Rolle «{name}»', { name })
        : e.subject || T('Ukjent person');
  return e.field === 'created' || e.field === 'deleted' ? of : `${of}: ${field}`;
}
const BREAK: Record<string, string> = {
  auto: 'trekkes alltid',
  punched: 'trekkes når den stemples',
  punched_min: 'stemplet, med fast lengde som minimum',
};

export function valueText(field: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return T('tom');
  if (typeof v === 'boolean') return v ? T('Ja') : T('Nei');
  if (field === 'break_mode' && typeof v === 'string') return T(BREAK[v] ?? v);
  if (typeof v === 'string' && WORD[field]?.[v]) return T(WORD[field][v] as string);
  if (field === 'agreed_week_hours' && typeof v === 'number') return `${String(v).replace('.', ',')} t`;
  // a day (an end date, the date a rate applies from) as the pages write it
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return fmtD(v);
  if (Array.isArray(v)) return T('{n, plural, one {# tilgang} other {# tilganger}}', { n: v.length });
  if (typeof v === 'object') return T('endret');
  return String(v);
}

export const when = (iso: string) =>
  new Intl.DateTimeFormat('nb-NO', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Oslo',
  }).format(new Date(iso));

export default function Endringer() {
  const q = useInfiniteQuery({
    queryKey: ['settings-history'],
    queryFn: ({ pageParam }) =>
      get<{ events: SettingChange[]; next: number | null }>(
        `/settings/history${pageParam ? `?before=${pageParam}` : ''}`,
      ),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const events = q.data?.pages.flatMap((p) => p.events) ?? [];
  return (
    <Panel
      title={T('Endringslogg')}
      sub={T(
        'Endringer i bedriftens innstillinger, lønnsarter, lønn og rolle per person, bemanningsbehov og roller, med hvem og når. Satsregler, arbeidsgiverkostnad og arbeidsmåte står ikke her.',
      )}
    >
      {q.isPending ? (
        <Skeleton rows={4} />
      ) : q.isError ? (
        <LoadError what={T('endringsloggen')} onRetry={() => q.refetch()} />
      ) : !events.length ? (
        <Empty title={T('Ingen endringer ennå')} />
      ) : (
        <>
          <ol className="stlog" data-testid="settings-history">
            {events.map((e) => (
              <li key={e.id}>
                <b>{changeLabel(e)}</b>
                <span>
                  {e.field === 'created'
                    ? T('Opprettet')
                    : e.field === 'deleted'
                      ? T('Slettet')
                      : grid(e.before) && grid(e.after)
                        ? T('endret') // a grid or a week of opening hours: the change, not two «endret»
                        : `${valueText(e.field, e.before)} → ${valueText(e.field, e.after)}`}
                </span>
                <small>
                  {whoText(e)}, {when(e.at)}
                </small>
              </li>
            ))}
          </ol>
          {q.hasNextPage && (
            <Button onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage}>
              {T('Vis eldre')}
            </Button>
          )}
        </>
      )}
    </Panel>
  );
}
